import type { Lot } from '../types/lot'
import type { FeedLog, FishStockLog, MortalityLog, WeightSampleLog } from '../types/production'
import { resolveCanonicalLotKey } from './lotIdentity'
import { lotApi } from './lotApi'
import { fishStockLogApi } from './fishStockLogApi'
import { mortalityLogApi } from './mortalityLogApi'
import { weightSampleLogApi } from './weightSampleLogApi'
import { feedLogApi } from './feedLogApi'
import { listProductionUnitTableRows, upsertProductionUnitTableRowAndSync, type BiomassDataRow } from './productionUnitTable'
import { computeCurrentBiomassPerLot } from './currentBiomassPerLot'
import { buildDeletedLotRowMatcher, loadDeletedLots } from './deletedLots'
import { createId } from './id'
import { buildBiomassFishStockMetadata } from '../screens/biomass/fishStockLogBridge'
import { ensureTodayProductionRow } from '../screens/crewMode/ensureTodayProductionRow'
import { farmDayKey } from './farmTime'

// Findings #183 / #178 — "Trasladar" into a unit that already holds the same
// lot: part (or all) of the fish join that lot. Neither lot changes unit; the
// server records the move (POST /lots/:id/move-fish), then each unit gets a
// count with its new stock on the move date (like "Sembrar en varias
// unidades"), and a source left with no fish is closed — in that order, so the
// outbox sends the counts while the source lot is still active.

const lotNameToken = (value: string | undefined): string => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''
  const canonical = resolveCanonicalLotKey(trimmed)
  return canonical === 'unknown-lot' ? trimmed.toLowerCase() : canonical
}

/** The active lot with the same name already living in `productionUnitId` (never the lot itself). */
export const findSameNameLotInUnit = (args: {
  lots: readonly Lot[]
  sourceLot: Pick<Lot, 'id' | 'lotKey'>
  farmId: string
  productionUnitId: string
}): Lot | undefined => {
  const token = lotNameToken(args.sourceLot.lotKey)
  if (!token || !args.farmId || !args.productionUnitId) return undefined
  return args.lots.find((lot) => (
    lot.id !== args.sourceLot.id
    && lot.farmId === args.farmId
    && lot.productionUnitId === args.productionUnitId
    && lot.stage === 'Active'
    && lotNameToken(lot.lotKey) === token
  ))
}

export type FishMovePlan =
  | { ok: true; fishMoved: number; sourceAfter: number | null; targetAfter: number | null; closesSource: boolean }
  | { ok: false; error: 'fishRequired' | 'fishMoreThanSource' }

/**
 * Stock on both sides after `fishMoved` fish pass from source to target.
 * Unknown stock stays unknown. Findings #204: `diedInMove` fish died during the
 * move — they leave the source as mortality and never reach the target.
 */
export const planFishMove = (args: { fishMoved: string | number; sourceStock: number | null; targetStock: number | null; diedInMove?: number }): FishMovePlan => {
  const raw = typeof args.fishMoved === 'number' ? String(args.fishMoved) : args.fishMoved.replace(/[.\s]/g, '').replace(',', '.')
  const fishMoved = Number(raw)
  if (!raw.trim() || !Number.isInteger(fishMoved) || fishMoved <= 0) return { ok: false, error: 'fishRequired' }
  const died = args.diedInMove ?? 0
  if (args.sourceStock !== null && fishMoved + died > args.sourceStock) return { ok: false, error: 'fishMoreThanSource' }
  const sourceAfter = args.sourceStock === null ? null : args.sourceStock - died - fishMoved
  return {
    ok: true,
    fishMoved,
    sourceAfter,
    targetAfter: args.targetStock === null ? null : args.targetStock + fishMoved,
    closesSource: sourceAfter === 0
  }
}

type UnitContext = {
  farmId: string
  farmName?: string
  productionUnitId: string
  productionUnitName?: string
}

const atOrBefore = (recordedAt: string | undefined, cutMs: number): boolean => {
  const ms = Date.parse(String(recordedAt ?? ''))
  return Number.isFinite(ms) && ms <= cutMs
}

/** The lot's fish in its unit at `asOf` (last count, less mortality since), or null when it has never been counted. */
export const loadLotStockAt = async (context: UnitContext & { lotId: string; asOf: string }): Promise<number | null> => (
  (await loadLotStockAndWeightAt(context)).stock
)

/**
 * The lot's fish and mean weight (g) in its unit at `asOf`; null where never
 * counted / weighed. Findings #210 uses both for the reduced ration after a move.
 */
export const loadLotStockAndWeightAt = async (context: UnitContext & { lotId: string; asOf: string }): Promise<{ stock: number | null; weightG: number | null }> => {
  const cutMs = Date.parse(context.asOf)
  if (!Number.isFinite(cutMs)) return { stock: null, weightG: null }
  const params = { farmId: context.farmId, productionUnitId: context.productionUnitId }
  const [rows, mortalityLogs, weightSampleLogs, fishStockLogs, deletedLots, farmLots] = await Promise.all([
    listProductionUnitTableRows(context).catch(() => [] as BiomassDataRow[]),
    mortalityLogApi.list(params).catch(() => [] as MortalityLog[]),
    weightSampleLogApi.list(params).catch(() => [] as WeightSampleLog[]),
    fishStockLogApi.list(params).catch(() => [] as FishStockLog[]),
    loadDeletedLots(context.farmId).catch(() => null),
    lotApi.list({ farmId: context.farmId }, { preferLocal: true }).catch(() => [] as Lot[])
  ])
  // Findings #207: a deleted lot's records never count for an active lot with the same name.
  const isDeletedLotRecord = buildDeletedLotRowMatcher(deletedLots ?? [], farmLots.map((lot) => ({
    id: String(lot.id),
    lotKey: String(lot.lotKey ?? ''),
    ...(lot.productionUnitId ? { productionUnitId: String(lot.productionUnitId) } : {}),
    ...(lot.createdAt ? { createdAt: String(lot.createdAt) } : {})
  })))
  const keep = (recordedAt: string | undefined, record: Parameters<typeof isDeletedLotRecord>[0]) => (
    atOrBefore(recordedAt, cutMs) && !isDeletedLotRecord(record, context.productionUnitId)
  )
  const current = computeCurrentBiomassPerLot({
    productionRows: rows.filter((row) => keep(String(row.date ?? ''), row)),
    mortalityLogs: mortalityLogs.filter((log) => keep(log.recordedAt, log)),
    weightSampleLogs: weightSampleLogs.filter((log) => keep(log.recordedAt, log)),
    fishStockLogs: fishStockLogs.filter((log) => keep(log.recordedAt, log))
  })
  const entry = current.get(context.lotId)
  const count = entry?.sourceStockCount
  const weight = entry?.sourceWeightG
  return {
    stock: typeof count === 'number' && Number.isFinite(count) ? Math.round(count) : null,
    weightG: typeof weight === 'number' && Number.isFinite(weight) && weight > 0 ? weight : null
  }
}

/**
 * Findings #178: the latest record of this lot in this unit dated after `after`
 * (count, mortality, weight sample or feeding). A whole-lot move dated before it
 * would leave that record outside the unit the lot was in.
 */
export const findLotRecordAfter = async (context: { farmId: string; productionUnitId: string; lotId: string; after: string }): Promise<string | null> => {
  const cutMs = Date.parse(context.after)
  if (!Number.isFinite(cutMs)) return null
  const params = { farmId: context.farmId, productionUnitId: context.productionUnitId, lotId: context.lotId }
  const [fishStock, mortality, weights, feeds] = await Promise.all([
    fishStockLogApi.list(params).catch(() => [] as FishStockLog[]),
    mortalityLogApi.list(params).catch(() => [] as MortalityLog[]),
    weightSampleLogApi.list(params).catch(() => [] as WeightSampleLog[]),
    feedLogApi.list(params).catch(() => [] as FeedLog[])
  ])
  let latest: { ms: number; recordedAt: string } | null = null
  for (const log of [...fishStock, ...mortality, ...weights, ...feeds]) {
    if (log.lotId !== context.lotId || log.productionUnitId !== context.productionUnitId) continue
    const ms = Date.parse(String(log.recordedAt ?? ''))
    if (!Number.isFinite(ms) || ms <= cutMs) continue
    if (!latest || ms > latest.ms) latest = { ms, recordedAt: log.recordedAt }
  }
  return latest?.recordedAt ?? null
}

const sameLocalDay = (left: string, right: string): boolean => {
  const a = new Date(left)
  const b = new Date(right)
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

/** One count on the move date: the same-day Biomasa row is updated in place, like an office Conteo. */
const recordMoveCount = async (context: UnitContext & { lot: Lot; recordedAt: string; count: number; notes: string; moveId?: string }): Promise<void> => {
  const rows = await listProductionUnitTableRows(context).catch(() => [] as BiomassDataRow[])
  const sameDay = rows.find((row) => String(row.lotId ?? '') === context.lot.id && sameLocalDay(String(row.date ?? ''), context.recordedAt))
  const id = sameDay ? String(sameDay.id) : createId('prod')
  const row: BiomassDataRow = sameDay
    ? { ...sameDay, id, date: context.recordedAt, fishStock: String(context.count), upVol: context.notes, stockMethod: 'exact' }
    : {
        id,
        date: context.recordedAt,
        lotId: context.lot.id,
        lot: context.lot.lotKey,
        stage: 'Active',
        weight: '',
        dailyMortality: '',
        fishStock: String(context.count),
        upVol: context.notes,
        health: '',
        healthDetail: '',
        stockMethod: 'exact'
      } as BiomassDataRow
  await upsertProductionUnitTableRowAndSync(context, row)
  await fishStockLogApi.create({
    farmId: context.farmId,
    productionUnitId: context.productionUnitId,
    lot: context.lot.lotKey,
    lotId: context.lot.id,
    productionRowId: id,
    recordedAt: context.recordedAt,
    countedStock: context.count,
    method: 'count',
    notes: context.notes,
    metadata: {
      ...buildBiomassFishStockMetadata({ productionRowId: id, lotId: context.lot.id, stage: 'Active' }),
      ...(context.moveId ? { fishMoveId: context.moveId } : {})
    }
  })
}

/**
 * Findings #179: a whole lot moved to a unit without that lot — "Cantidad que
 * llega" becomes its count in the new unit on the move date. Call only after
 * the move reached the server, so the count is never refused for being dated
 * before the lot was there.
 */
export const recordArrivalCount = (context: UnitContext & { lot: Lot; recordedAt: string; count: number; notes: string }): Promise<void> => (
  recordMoveCount(context)
)

/** "Cantidad que llega": empty = no count; otherwise a whole number ≥ 0. */
export const parseArrivalCount = (value: string): { ok: true; count: number | null } | { ok: false } => {
  const raw = value.replace(/[.\s]/g, '').replace(',', '.')
  if (!raw) return { ok: true, count: null }
  const count = Number(raw)
  return Number.isInteger(count) && count >= 0 ? { ok: true, count } : { ok: false }
}

/** Findings #204: "Murieron en el traslado" — empty = none; otherwise a whole number ≥ 0. */
export const parseMoveDeaths = (value: string): { ok: true; count: number } | { ok: false } => {
  const parsed = parseArrivalCount(value)
  return parsed.ok ? { ok: true, count: parsed.count ?? 0 } : { ok: false }
}

/**
 * Findings #204: fish that died during a move are a mortality of the unit they
 * left, recorded one minute before the move (cause Manipulación, as Crew Mode
 * records it), so the move starts from the stock that really left.
 */
export const recordMoveDeaths = async (context: UnitContext & { lot: Pick<Lot, 'id' | 'lotKey'>; moveAt: string; count: number; notes: string; timeZone: string }): Promise<void> => {
  if (!(context.count > 0)) return
  const recordedAt = new Date(Date.parse(context.moveAt) - 60_000).toISOString()
  await mortalityLogApi.create({
    farmId: context.farmId,
    productionUnitId: context.productionUnitId,
    lot: context.lot.lotKey,
    lotId: context.lot.id,
    recordedAt,
    mortalityCount: context.count,
    notes: context.notes,
    metadata: { observedCause: 'handling', moveLoss: true }
  })
  await ensureTodayProductionRow(
    { farmId: context.farmId, farmName: context.farmName, productionUnitId: context.productionUnitId, productionUnitName: context.productionUnitName },
    { id: context.lot.id, lotKey: context.lot.lotKey },
    farmDayKey(recordedAt, context.timeZone) ?? undefined
  ).catch(() => {})
}

export class FishMoveOfflineError extends Error {
  constructor() {
    super('Moving fish into another lot needs a connection.')
    this.name = 'FishMoveOfflineError'
  }
}

export type RecordFishMoveArgs = {
  sourceLot: Lot
  targetLot: Lot
  source: UnitContext
  target: UnitContext
  fishMoved: number
  sourceAfter: number | null
  targetAfter: number | null
  recordedAt: string
  /** Shown on both counts, e.g. "Traslado: 3.000 peces de R7 a B2". */
  countNotes: string
  notes?: string
  /** Findings #204: fish that died during the move (mortality in the source unit). */
  diedInMove?: { count: number; notes: string; timeZone: string }
}

export const recordFishMove = async (args: RecordFishMoveArgs): Promise<{ closedSource: boolean }> => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) throw new FishMoveOfflineError()
  const moveId = createId('lineage')
  await lotApi.moveFishRemote(args.sourceLot.id, {
    targetLotId: args.targetLot.id,
    fishMoved: args.fishMoved,
    eventId: moveId,
    recordedAt: args.recordedAt,
    notes: args.notes?.trim() || undefined
  })
  if (args.diedInMove && args.diedInMove.count > 0) {
    await recordMoveDeaths({ ...args.source, lot: args.sourceLot, moveAt: args.recordedAt, ...args.diedInMove })
  }
  if (args.sourceAfter !== null) {
    await recordMoveCount({ ...args.source, lot: args.sourceLot, recordedAt: args.recordedAt, count: args.sourceAfter, notes: args.countNotes, moveId })
  }
  if (args.targetAfter !== null) {
    await recordMoveCount({ ...args.target, lot: args.targetLot, recordedAt: args.recordedAt, count: args.targetAfter, notes: args.countNotes, moveId })
  }
  const closedSource = args.sourceAfter === 0
  if (closedSource) {
    await lotApi.update(args.sourceLot.id, { stage: 'Inactive' })
  }
  return { closedSource }
}
